import { isUsableChatWidgetId } from '../utils/support-chat-widget-id.utils'
import {
  SUPPORT_CHAT_WIDGET_MOUNT_ID,
  attachSupportChatWidgetDomHooks,
  clearSupportChatWidgetMount,
  getSupportChatMountedEmbedKey,
  isSupportChatWidgetInDocument,
  serializeSupportChatEmbedMountKey,
  setSupportChatMountedEmbedKey,
} from '../utils/supportChatWidgetDom'
import type { UseQueryReturnType } from '@tanstack/vue-query'

import type { AuthStore, LocationStore, QueryStore, SupportChatEmbedWatchCtx } from '../types/support-chat-embed-watch.types'

export function unknownToError(unknownError: unknown): Error {
  if (unknownError instanceof Error) return unknownError
  if (typeof unknownError === 'object' && unknownError !== null) {
    return new Error(JSON.stringify(unknownError))
  }
  return new Error(String(unknownError))
}

export function isSupportChatEmbedSessionInvalid(authStore: AuthStore, locationStore: LocationStore): boolean {
  return !authStore.isLoggedIn || !locationStore.locationId || !isUsableChatWidgetId(locationStore.chatWidgetSettings.widgetId)
}

export function clearSupportChatEmbedSession(queryStore: QueryStore): void {
  queryStore.clearSupportChatWidgetCache()
  clearSupportChatWidgetMount()
}

/** True when the async watch cycle should stop because the embed query is still pending with no data. */
export function shouldStopForEmbedQueryPending(embedQuery: UseQueryReturnType<string | null, Error>): boolean {
  if (embedQuery.fetchStatus.value === 'fetching' && embedQuery.data.value === undefined) {
    clearSupportChatWidgetMount()
  }
  return embedQuery.isPending.value && embedQuery.data.value === undefined
}

export function handleSupportChatEmbedQueryError(ctx: SupportChatEmbedWatchCtx): boolean {
  const { embedQuery, queryStore, locationStore, captureException, signal, generation, currentGeneration } = ctx
  if (!embedQuery.isError.value) return false

  const err = embedQuery.error.value
  if (err) captureException(err instanceof Error ? err : unknownToError(err))
  queryStore.removeSupportChatEmbedCache(locationStore.locationId, locationStore.chatWidgetSettings.widgetId)
  if (!signal.aborted && generation === currentGeneration()) {
    clearSupportChatWidgetMount()
  }
  return true
}

export function handleSupportChatBlankEmbedSuccess(ctx: SupportChatEmbedWatchCtx, html: string | null): boolean {
  const { embedQuery, queryStore, locationStore } = ctx
  const isBlank = html === null || html === ''
  if (!embedQuery.isSuccess.value || !isBlank) return false

  queryStore.removeSupportChatEmbedCache(locationStore.locationId, locationStore.chatWidgetSettings.widgetId)
  clearSupportChatWidgetMount()
  return true
}

export function isSupportChatWatchStale(signal: AbortSignal, generation: number, currentGeneration: () => number): boolean {
  return signal.aborted || generation !== currentGeneration()
}

export async function injectSupportChatEmbedWithPostscribe(html: string, signal: AbortSignal, embedMountKey: string): Promise<void> {
  clearSupportChatWidgetMount()
  const { default: runPostscribe } = await import('postscribe')

  if (signal.aborted) return

  runPostscribe(`#${SUPPORT_CHAT_WIDGET_MOUNT_ID}`, html, {
    done: () => {
      if (signal.aborted) return
      setSupportChatMountedEmbedKey(embedMountKey)
      attachSupportChatWidgetDomHooks()
    },
  })
}

export async function tryInjectSupportChatEmbed(ctx: SupportChatEmbedWatchCtx, html: string): Promise<void> {
  const { captureException, queryStore, locationStore, signal, generation, currentGeneration } = ctx
  const embedMountKey = serializeSupportChatEmbedMountKey(locationStore.locationId, locationStore.chatWidgetSettings.widgetId)
  try {
    await injectSupportChatEmbedWithPostscribe(html, signal, embedMountKey)
  } catch (unknownError: unknown) {
    captureException(unknownToError(unknownError))
    if (!signal.aborted && generation === currentGeneration()) {
      queryStore.removeSupportChatEmbedCache(locationStore.locationId, locationStore.chatWidgetSettings.widgetId)
      clearSupportChatWidgetMount()
    }
  }
}

export async function runSupportChatEmbedWatchCycle(ctx: SupportChatEmbedWatchCtx): Promise<void> {
  const { authStore, locationStore, embedFetchEnabled, embedQuery, queryStore } = ctx

  if (isSupportChatEmbedSessionInvalid(authStore, locationStore)) {
    clearSupportChatEmbedSession(queryStore)
    return
  }

  if (!embedFetchEnabled.value) {
    clearSupportChatWidgetMount()
    return
  }

  if (shouldStopForEmbedQueryPending(embedQuery)) return
  if (handleSupportChatEmbedQueryError(ctx)) return

  const html = embedQuery.data.value ?? null
  if (handleSupportChatBlankEmbedSuccess(ctx, html)) return
  if (!html) return

  const { signal, generation, currentGeneration } = ctx
  if (isSupportChatWatchStale(signal, generation, currentGeneration)) return

  const embedMountKey = serializeSupportChatEmbedMountKey(locationStore.locationId, locationStore.chatWidgetSettings.widgetId)
  if (isSupportChatWidgetInDocument() && getSupportChatMountedEmbedKey() === embedMountKey) {
    attachSupportChatWidgetDomHooks()
    return
  }

  await tryInjectSupportChatEmbed(ctx, html)
}
